import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CircleNotch, Function } from "@phosphor-icons/react";
import { OverlayTile } from "./OverlayTile";
import { toast } from "sonner";

export default function PcaPanel({ project, reload, setOverlay, activeOverlayKey }) {
  const id = project.id;
  const [job, setJob] = useState(project.pca_job);
  const pca = project.pca;
  const bbox = project.boundary_metrics?.bbox;
  const timer = useRef(null);
  const hasScene = !!project.selected_scene?.assets;

  useEffect(() => {
    const s = job?.status;
    if (s && !["Completed", "Failed"].includes(s)) {
      timer.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/projects/${id}/pca`);
          setJob(data.pca_job);
          if (["Completed", "Failed"].includes(data.pca_job?.status)) {
            clearInterval(timer.current); reload();
            toast[data.pca_job.status === "Completed" ? "success" : "error"](
              data.pca_job.status === "Completed" ? "PCA / Crosta alteration maps ready" : "PCA failed");
          }
        } catch (_) { /* ignore */ }
      }, 4000);
    }
    return () => clearInterval(timer.current);
  }, [job?.status, id]);

  const start = async () => {
    try {
      await api.post(`/projects/${id}/pca/start`);
      setJob({ status: "Queued", stage: "Queued", progress: 0 });
      toast.info("PCA queued");
    } catch (e) { toast.error(apiError(e)); }
  };
  const running = job && !["Completed", "Failed"].includes(job.status);

  return (
    <div className="border-t border-border pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="overline">PCA / Crosta Alteration Mapping</p>
        <Badge className="bg-primary/15 text-primary text-[9px]">Advanced</Badge>
      </div>
      <Button className="w-full" onClick={start} disabled={running || !hasScene} data-testid="pca-start">
        {running ? <><CircleNotch size={16} className="mr-1 animate-spin" />Computing components…</> : <><Function size={16} className="mr-1" weight="fill" />Run PCA &amp; Crosta PCA</>}
      </Button>
      {!hasScene && <p className="text-[11px] text-muted-foreground">Select a satellite scene first.</p>}
      {job && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm"><span>{job.stage || job.status}</span>
            <Badge variant="outline">{job.status}</Badge></div>
          <Progress value={job.progress || 0} className="h-2" />
          {job.error && <div className="text-xs text-destructive font-mono border border-destructive/40 rounded p-2">{job.error}</div>}
        </div>
      )}
      {pca && (
        <div className="space-y-3">
          <div>
            <p className="overline mb-1">Directed (Crosta) alteration</p>
            <div className="grid grid-cols-2 gap-2">
              {(pca.crosta || []).map((c, i) => (
                <OverlayTile prefix="pca" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} key={i} k={`pca:crosta:${i}`} name={c.name} png={c.png} sub={`${c.component} · ${c.variance_pct}% var`} />
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-1">Principal components</p>
            <div className="grid grid-cols-2 gap-2">
              {(pca.components || []).map((c) => (
                <OverlayTile prefix="pca" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} key={c.name} k={`pca:${c.name}`} name={c.name} png={c.png} sub={`${c.variance_pct}% var`} />
              ))}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{pca.note}</p>
        </div>
      )}
    </div>
  );
}
